import React from 'react';
import { Select } from 'antd';
import { parseDisplayTextToAttribute, parseDisplayValuesToAttributes } from 'pages/UserPage/components/MiddlePane/utils/displayAttributes';
import { AddedAttributeType, AddedAttribute, AddedSection } from '../../../../../types';
import {
  updateSingletonValue,
  updateValue,
} from '../../../../updateAttributeValueUtils';
import styles from '../../AttributeTypeEditField.module.scss';
import AttributeTag from '../AttributeTag';
import { getListableAttributeOptions, getListableAttributeTagValues } from './listableEditFieldUtils';

interface Props {
  section: AddedSection;
  attributesForEdit: AddedAttribute[];
  hasDerivedAttributes: boolean;
  attributeType: AddedAttributeType;
  userAttributeTypes: AddedAttributeType[];
  sections: AddedSection[];
  setUserAttributeTypes: (userAttributeTypes: AddedAttributeType[]) => void;
}

/**
 * This component is for editing a listable attribute type.
 *
 * Some key aspects of this component is that multiple tags can be displayed in the field,
 * which may be the non-derived attributes that users can selected, as well as derives attributes.
 * Additionally, the tags can be primary or secondary attributes (displayed as different colors).
 *
 * Note that in the dropdown, only non-derived, primary attributes are displayed.
 *
 * In order to distinguish between primary and secondary attributes, as well as derived attributes,
 * the "value" prop of the Select component is a "display value" (which is a stringified version of the
 * attribute), rather than a "display text".
 *
 * The <AttributeTag> component is used to render the tags, and it can handle both display values and
 * display texts.
 */
const ListableEditField = ({
  section,
  attributesForEdit,
  hasDerivedAttributes,
  attributeType,
  userAttributeTypes,
  sections,
  setUserAttributeTypes,
}: Props) => {
  const isSingleton = attributeType.singleton;
  const isUsingDisplayValue = !isSingleton;

  // the value of the tags in the field
  const tagValues = getListableAttributeTagValues(attributesForEdit, isUsingDisplayValue);

  // the options in the dropdown menu
  const dropdownOptions = getListableAttributeOptions(section.name, attributeType.name, sections, isUsingDisplayValue);

  return (
    <Select<string | string[]>
      allowClear={!hasDerivedAttributes}
      className={styles.editField}
      getPopupContainer={e => e}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      mode={!attributeType.singleton ? 'multiple' : 'default'}
      onChange={(input: string | string[]) => {
        if (attributeType.singleton) {
          // update with a single attribute
          const attr = parseDisplayTextToAttribute(input as string);
          updateSingletonValue(
            userAttributeTypes,
            setUserAttributeTypes,
            attributeType.name,
            attr
          );
        } else {
          // update with multiple attributes
          const attrs = parseDisplayValuesToAttributes(input as string[]);
          updateValue(
            attributeType.name,
            attrs,
            userAttributeTypes,
            setUserAttributeTypes
          );
        }
      }}
      // unfortunately, we need to go against React recommendation in order to reuse
      // the AttributeTag component here
      // eslint-disable-next-line react/no-unstable-nested-components
      tagRender={props => (
        <AttributeTag
          {...props}
          attributeTypeName={attributeType.name}
          attributesForEdit={attributesForEdit}
          isUsingDisplayValues={isUsingDisplayValue}
          setUserAttributeTypes={setUserAttributeTypes}
          userAttributeTypes={userAttributeTypes}
        />
      )}
      value={tagValues}
    >
      {dropdownOptions}
    </Select>
  );
};

export default ListableEditField;
